import * as pdfjsLib from 'pdfjs-dist';
import jsPDF from 'jspdf';
import defaultLogo from '../watermark.svg';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.mjs`;

// Helper functions (defined first to be available to all other functions)

async function loadWatermarkImage() {
  const logoImg = new Image();
  logoImg.src = defaultLogo;
  await logoImg.decode();
  return logoImg;
}

function applyWatermark(canvas, context, logoImg, density = 1.0) {
  const tileWidth = canvas.width * 0.15;
  const tileHeight = tileWidth * (logoImg.height / logoImg.width);
  // Use density to calculate spacing: higher density = smaller spacing
  const spacingX = tileWidth * (2 / density);
  const spacingY = tileHeight * (2 / density);

  context.globalAlpha = 0.25;
  for (let y = -tileHeight; y < canvas.height + tileHeight; y += spacingY) {
    for (let x = -tileWidth; x < canvas.width + tileWidth; x += spacingX) {
      context.save();
      context.translate(x + tileWidth / 2, y + tileHeight / 2);
      context.rotate(-Math.PI / 6);
      context.drawImage(logoImg, -tileWidth / 2, -tileHeight / 2, tileWidth, tileHeight);
      context.restore();
    }
  }
  context.globalAlpha = 1;
}

function addGrain(imageData, intensity = 50) {
  if (intensity === 0) return imageData;
  for (let i = 0; i < imageData.data.length; i += 4) {
    const noise = (Math.random() - 0.5) * intensity;
    imageData.data[i] = Math.max(0, Math.min(255, imageData.data[i] + noise));
    imageData.data[i + 1] = Math.max(0, Math.min(255, imageData.data[i + 1] + noise));
    imageData.data[i + 2] = Math.max(0, Math.min(255, imageData.data[i + 2] + noise));
  }
  return imageData;
}

// Preview generation functions (for configuration dialog)

async function generatePdfPreview(url, options = {}) {
  const { density = 1.0, grainIntensity = 50 } = options;

  const response = await fetch(url);
  const arrayBuffer = await response.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const logoImg = await loadWatermarkImage();

  // Only render first page for preview
  const page = await pdf.getPage(1);
  // Use lower scale for preview
  const viewport = page.getViewport({ scale: 1.5 });

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({ canvasContext: context, viewport }).promise;

  // Apply watermark with density
  applyWatermark(canvas, context, logoImg, density);

  // Add grain with intensity
  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  addGrain(imageData, grainIntensity);
  context.putImageData(imageData, 0, 0);

  return canvas.toDataURL('image/jpeg', 0.8);
}

async function generateImagePreview(url, options = {}) {
  const { density = 1.0, grainIntensity = 50 } = options;

  const logoImg = await loadWatermarkImage();

  const image = new Image();
  image.crossOrigin = 'anonymous';

  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = reject;
    image.src = url;
  });

  // Scale down for preview if image is large
  const maxPreviewWidth = 800;
  const scale = image.width > maxPreviewWidth ? maxPreviewWidth / image.width : 1;

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = image.width * scale;
  canvas.height = image.height * scale;
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  // Apply watermark with density
  applyWatermark(canvas, context, logoImg, density);

  // Add grain with intensity
  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  addGrain(imageData, grainIntensity);
  context.putImageData(imageData, 0, 0);

  return canvas.toDataURL('image/jpeg', 0.8);
}

/**
 * Generate a preview of the watermarked first page (for configuration dialog)
 * @param {string} url - URL of the original document
 * @param {string} fileType - MIME type of the file
 * @param {Object} options - Watermark configuration options
 * @param {number} options.density - Watermark density (0.3 = sparse, 1.0 = default, 2.0 = dense)
 * @param {number} options.grainIntensity - Grain intensity (0-100)
 * @returns {Promise<string>} - Data URL of the preview image
 */
export async function generateWatermarkPreview(url, fileType, options = {}) {
  const isPdf = fileType === 'application/pdf' || url?.toLowerCase().endsWith('.pdf');
  const isImage = fileType?.startsWith('image/') || url?.match(/\.(jpg|jpeg|png|gif|webp)$/i);

  const mergedOptions = {
    density: options.density ?? 1.0,
    grainIntensity: options.grainIntensity ?? 50,
  };

  if (isPdf) {
    return await generatePdfPreview(url, mergedOptions);
  } else if (isImage) {
    return await generateImagePreview(url, mergedOptions);
  } else {
    throw new Error('Unsupported file type');
  }
}

// Full document generation functions

async function generateWatermarkedPdf(url, options = {}) {
  const { density = 1.0, grainIntensity = 50 } = options;

  const response = await fetch(url);
  const arrayBuffer = await response.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const logoImg = await loadWatermarkImage();

  const jspdf = new jsPDF({ unit: 'pt', format: 'a4' });

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale: 2 });

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    await page.render({ canvasContext: context, viewport }).promise;

    // Apply watermark with density
    applyWatermark(canvas, context, logoImg, density);

    // Add grain with intensity
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    addGrain(imageData, grainIntensity);
    context.putImageData(imageData, 0, 0);

    // Compress if needed
    const maxCompressWidth = 2000;
    let compressCanvas = canvas;
    if (canvas.width > maxCompressWidth) {
      compressCanvas = document.createElement('canvas');
      const scale = maxCompressWidth / canvas.width;
      compressCanvas.width = maxCompressWidth;
      compressCanvas.height = canvas.height * scale;
      const ctx = compressCanvas.getContext('2d');
      ctx.filter = 'blur(1.5px)';
      ctx.drawImage(canvas, 0, 0, compressCanvas.width, compressCanvas.height);
      ctx.filter = 'none';
    }

    // Add to PDF
    const imgData = compressCanvas.toDataURL('image/jpeg', 0.7);
    const pageWidth = jspdf.internal.pageSize.getWidth();
    const pageHeight = jspdf.internal.pageSize.getHeight();

    let width = compressCanvas.width;
    let height = compressCanvas.height;
    const ratio = Math.min(pageWidth / width, pageHeight / height);
    width *= ratio;
    height *= ratio;

    if (i > 1) jspdf.addPage();
    jspdf.addImage(imgData, 'JPEG', 0, 0, width, height);
  }

  return jspdf.output('blob');
}

async function generateWatermarkedImage(url, options = {}) {
  const { density = 1.0, grainIntensity = 50 } = options;

  const logoImg = await loadWatermarkImage();

  const image = new Image();
  image.crossOrigin = 'anonymous';

  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = reject;
    image.src = url;
  });

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = image.width;
  canvas.height = image.height;
  context.drawImage(image, 0, 0);

  // Apply watermark with density
  applyWatermark(canvas, context, logoImg, density);

  // Add grain with intensity
  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  addGrain(imageData, grainIntensity);
  context.putImageData(imageData, 0, 0);

  // Compress if needed
  const maxCompressWidth = 2000;
  let compressCanvas = canvas;
  if (canvas.width > maxCompressWidth) {
    compressCanvas = document.createElement('canvas');
    const scale = maxCompressWidth / canvas.width;
    compressCanvas.width = maxCompressWidth;
    compressCanvas.height = canvas.height * scale;
    const ctx = compressCanvas.getContext('2d');
    ctx.filter = 'blur(1.5px)';
    ctx.drawImage(canvas, 0, 0, compressCanvas.width, compressCanvas.height);
  }

  // Convert to PDF
  const jspdf = new jsPDF({ unit: 'pt', format: 'a4' });
  const imgData = compressCanvas.toDataURL('image/jpeg', 0.7);
  const pageWidth = jspdf.internal.pageSize.getWidth();
  const pageHeight = jspdf.internal.pageSize.getHeight();

  let width = compressCanvas.width;
  let height = compressCanvas.height;
  const ratio = Math.min(pageWidth / width, pageHeight / height);
  width *= ratio;
  height *= ratio;

  jspdf.addImage(imgData, 'JPEG', 0, 0, width, height);

  return jspdf.output('blob');
}

/**
 * Generate a watermarked version of a PDF or image
 * @param {string} url - URL of the original document
 * @param {string} fileType - MIME type of the file
 * @param {Object} options - Watermark configuration options
 * @param {number} options.density - Watermark density (0.3 = sparse, 1.0 = default, 2.0 = dense)
 * @param {number} options.grainIntensity - Grain intensity (0-100)
 * @returns {Promise<Blob>} - Watermarked PDF blob
 */
export async function generateWatermarkedDocument(url, fileType, options = {}) {
  const isPdf = fileType === 'application/pdf' || url?.toLowerCase().endsWith('.pdf');
  const isImage = fileType?.startsWith('image/') || url?.match(/\.(jpg|jpeg|png|gif|webp)$/i);

  const mergedOptions = {
    density: options.density ?? 1.0,
    grainIntensity: options.grainIntensity ?? 50,
  };

  if (isPdf) {
    return await generateWatermarkedPdf(url, mergedOptions);
  } else if (isImage) {
    return await generateWatermarkedImage(url, mergedOptions);
  } else {
    throw new Error('Unsupported file type');
  }
}
